
























import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import VSlottedSelect, {
  SlottedSelectItem,
  SlottedSelectGroup,
} from "@/components/VSlottedSelect.vue";
import { MediumCoverage, MediumType } from "@/models";
import { MediumReach, MediumTypology } from "@/models/MediumType";
import MediumSelectorItem from "./MediumSelectorItem.vue";
import MediumTypeService from "@/service/MediumTypeService";
import VSignPost from "@/components/Company/VSignPost.vue";

type TypologyItemMapper = (typology: MediumTypology) => SlottedSelectItem;

@Component({ components: { VSlottedSelect, MediumSelectorItem } })
export default class MediumSelector extends Vue {
  @Prop({ default: null }) readonly mediumTypeId!: string | null;
  @Prop({ default: null }) readonly reach!: string | null;
  @Prop({
    default: () => {
      return [];
    },
  })
  readonly coverages!: MediumCoverage[];
  groups: SlottedSelectGroup[] = [];
  selected: SlottedSelectItem | null = null;
  mediumTypes: MediumTypology[] = [];

  @Watch("coverages")
  onCoveragesChanged(value: MediumCoverage[]) {
    this.init();
  }

  async mounted() {
    this.mediumTypes = await MediumTypeService.getMediumTypes();
    this.init();
  }

  onItemSelected(item: SlottedSelectItem, group?: SlottedSelectGroup) {
    this.$emit("selected", item.id, group?.id);
  }

  private init() {
    this.generateGroups();
    this.initSelected();
  }

  private generateGroups() {
    this.groups = MediumType.reaches.map(this.mediumReachToGroup);
  }

  private mediumReachToGroup(reach: MediumReach): SlottedSelectGroup {
    const group: SlottedSelectGroup = {
      id: reach.id,
      name: reach.name,
      items: [],
    };

    const typologyToItem = this.typologyItemMapperFromGroupAndCoverages(
      group,
      this.coverages
    );
    const sortByPos = (a: MediumTypology, b: MediumTypology) =>
      a.position - b.position;

    if (group.id === "national") {
      group.items = this.mediumTypes
        .filter(this.nationalTypologies)
        .sort(sortByPos)
        .map(typologyToItem);
    } else if (group.id === "local") {
      group.items = this.mediumTypes
        .filter(this.localTypologies)
        .sort(sortByPos)
        .map(typologyToItem);
    }

    return group;
  }

  private nationalTypologies(typology: MediumTypology): boolean {
    return typology.reach === "national";
  }

  private localTypologies(typology: MediumTypology): boolean {
    return typology.reach === "local";
  }

  private typologyItemMapperFromGroupAndCoverages(
    group: SlottedSelectGroup,
    coverages: MediumCoverage[]
  ): TypologyItemMapper {
    return (typology: MediumTypology) => {
      const coveragesCount = this.coveragesCount(coverages, typology.id);
      return {
        id: typology.id,
        text: `${typology.name}${this.coverageCountToString(coveragesCount)}`,
        icon: typology.icon,
        additional: this.mediumTypeUnitOfMeasurement(
          typology.unitOfMeasurement
        ),
        croppedLogo: typology.croppedLogo
          ? VSignPost.removeHeadingBackSlash(typology.croppedLogo)
          : "",
      };
    };
  }

  private mediumTypeUnitOfMeasurement(name: string): string | null {
    return MediumType.unitOfMeasurements[name] || null;
  }

  private coveragesCount(
    coverages: MediumCoverage[],
    mediumTypeId: string
  ): number {
    return coverages.filter((coverage) => {
      return coverage.mediumTypeId === mediumTypeId;
    }).length;
  }

  private coverageCountToString(count: number): string {
    if (count === 0) {
      return "";
    }
    return ` (${count})`;
  }

  private initSelected() {
    const reach = this.reach || "national";
    const group = this.groups.find((g) => g.id === reach);
    if (this.mediumTypeId) {
      this.selected =
        group?.items.find((i) => i.id === this.mediumTypeId) || null;
    } else {
      const defaultMediumType = this.mediumTypes.find(
        (mt) => mt.reach === reach && mt.typology === "tv"
      );
      this.selected =
        group?.items.find((i) => i.id === defaultMediumType!.id) || null;
    }
    this.$emit("selected", this.selected?.id, reach);
  }
}
